import { ArrowRight, Boxes, Building2, Database, FolderKanban, HardDriveUpload, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ROLE_LABELS } from '@hyphen-db-studio/access-core';
import { clearLegacyData, hasLegacyData } from '../lib/persistence';
import { navigateToOrganization } from '../App';
import AppHeader from '../components/workspace/AppHeader';
import NameDialog from '../components/workspace/NameDialog';
import { useWorkspaceStore } from '../store/useWorkspaceStore';

const ACCENTS = [
  'from-sky-500 to-cyan-400',
  'from-violet-500 to-fuchsia-400',
  'from-emerald-500 to-teal-400',
  'from-amber-500 to-orange-400',
  'from-rose-500 to-pink-400',
];

export default function Dashboard() {
  const organizations = useWorkspaceStore((s) => s.organizations);
  const projects = useWorkspaceStore((s) => s.projects);
  const createOrganization = useWorkspaceStore((s) => s.createOrganization);
  const removeOrganization = useWorkspaceStore((s) => s.removeOrganization);
  const importLegacyData = useWorkspaceStore((s) => s.importLegacyData);
  const [showNew, setShowNew] = useState(false);
  const [legacyAvailable, setLegacyAvailable] = useState(() => hasLegacyData());
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete organization "${name}" and all its projects and diagrams? This cannot be undone.`)) return;
    try {
      await removeOrganization(id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete the organization');
    }
  }

  async function handleImport() {
    setImporting(true);
    setImportError(null);
    try {
      await importLegacyData();
      setLegacyAvailable(false);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  function discardLegacy() {
    if (!confirm('Discard the diagrams saved in this browser before accounts existed? This cannot be undone.')) return;
    clearLegacyData();
    setLegacyAvailable(false);
  }

  return (
    <div className="page-bg">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-6 pb-16 pt-12">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight dark:text-white">Your organizations</h1>
            <p className="mt-2 max-w-xl text-gray-500 dark:text-gray-400">
              Design database schemas and system architecture together. Organizations hold projects, and projects hold your diagrams.
            </p>
          </div>
          {organizations.length > 0 && (
            <button onClick={() => setShowNew(true)} className="btn-primary">
              <Plus className="h-4 w-4" /> New organization
            </button>
          )}
        </div>

        {legacyAvailable && (
          <div className="surface mb-8 flex flex-wrap items-center gap-4 border-sky-200 bg-sky-50/60 p-4 dark:border-sky-500/30 dark:bg-sky-500/10">
            <HardDriveUpload className="h-5 w-5 shrink-0 text-sky-600 dark:text-sky-300" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold dark:text-gray-100">Diagrams found in this browser</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                You created these before accounts existed. Import them into your account to keep working on them.
              </div>
              {importError && <div className="mt-1 text-sm text-red-600 dark:text-red-400">{importError}</div>}
            </div>
            <button onClick={discardLegacy} className="btn-ghost" disabled={importing}>
              Discard
            </button>
            <button onClick={handleImport} className="btn-primary" disabled={importing}>
              {importing ? 'Importing…' : 'Import'}
            </button>
          </div>
        )}

        {organizations.length === 0 ? (
          <div className="surface relative overflow-hidden px-8 py-16 text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-lg shadow-sky-500/30">
              <Building2 className="h-8 w-8" />
            </div>
            <h2 className="text-xl font-semibold dark:text-white">Create your first organization</h2>
            <p className="mx-auto mb-8 mt-2 max-w-md text-gray-500 dark:text-gray-400">
              It's the home for your team's projects. Inside each project you can build ER diagrams and architecture diagrams.
            </p>
            <div className="mx-auto mb-8 flex max-w-lg items-center justify-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 dark:bg-white/10">
                <Building2 className="h-3.5 w-3.5" /> Organization
              </span>
              <ArrowRight className="h-3.5 w-3.5" />
              <span className="flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 dark:bg-white/10">
                <FolderKanban className="h-3.5 w-3.5" /> Project
              </span>
              <ArrowRight className="h-3.5 w-3.5" />
              <span className="flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 dark:bg-white/10">
                <Database className="h-3.5 w-3.5" /> <Boxes className="h-3.5 w-3.5" /> Diagrams
              </span>
            </div>
            <button onClick={() => setShowNew(true)} className="btn-primary px-5 py-2.5">
              <Plus className="h-4 w-4" /> Create organization
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {organizations.map((org, i) => {
              const orgProjects = projects.filter((p) => p.orgId === org.id);
              const diagrams = orgProjects.reduce((n, p) => n + p.erIds.length + p.archIds.length, 0);
              return (
                <div key={org.id} onClick={() => navigateToOrganization(org.id)} className="surface-hover group relative p-5">
                  {org.role === 'owner' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(org.id, org.name);
                      }}
                      className="absolute right-3 top-3 rounded-lg p-1.5 text-gray-400 opacity-0 transition hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                      title="Delete organization"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  <div
                    className={`mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br text-lg font-bold text-white shadow-md ${ACCENTS[i % ACCENTS.length]}`}
                  >
                    {org.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="truncate text-base font-semibold dark:text-gray-100">{org.name}</div>
                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-white/10 dark:text-gray-400">
                      {ROLE_LABELS[org.role]}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                    <span>
                      {orgProjects.length} {orgProjects.length === 1 ? 'project' : 'projects'}
                    </span>
                    <span className="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                    <span>
                      {diagrams} {diagrams === 1 ? 'diagram' : 'diagrams'}
                    </span>
                  </div>
                  <ArrowRight className="absolute bottom-5 right-5 h-4 w-4 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-sky-500" />
                </div>
              );
            })}
          </div>
        )}
      </main>

      {showNew && (
        <NameDialog
          title="New organization"
          description="A workspace for your team's projects."
          placeholder="e.g. Acme Inc."
          onCancel={() => setShowNew(false)}
          onConfirm={async (name) => {
            const org = await createOrganization(name);
            setShowNew(false);
            navigateToOrganization(org.id);
          }}
        />
      )}
    </div>
  );
}
